import React, { useEffect, useState } from 'react';
import {
  Grid,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  TextField,
  MenuItem,
} from '@mui/material';
import { Edit, Delete, Download } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import { Axios } from 'services/axios';
import { Loading, Notify } from 'notiflix';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const DesignOrders = () => {
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const [orders, setOrders] = useState([]);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const {
    register: registerStatus,
    handleSubmit: handleSubmitStatus,
    reset: resetStatus,
    setValue: setValueStatus,
    formState: { errors: statusErrors },
  } = useForm();

  // Fetch all design orders
  const fetchOrders = async () => {
    if (!canRead) return;
    try {
      Loading.circle('Loading design orders...');
      const response = await Axios.fetch('design/admin/design-orders');
      setOrders(response.data.data || []);
    } catch (error) {
      Notify.failure('Failed to fetch design orders');
    } finally {
      Loading.remove();
    }
  };

  // Update order status (admin only)
  const handleStatusUpdate = async (data) => {
    Loading.circle('Updating order status...');
    try {
      await Axios.patch(`design/admin/design-orders/${selectedOrder.id}/status`, {
        status: data.status,
      });
      Notify.success('Order status updated!');
      setOpenStatusDialog(false);
      resetStatus();
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to update order status');
    } finally {
      Loading.remove();
    }
  };

  const handleEditStatus = (order) => {
    setSelectedOrder(order);
    setValueStatus('status', order.status || 'pending');
    setOpenStatusDialog(true);
  };

  // Download design files
  const handleDownload = async (designItemId, orderId) => {
    try {
      Loading.circle('Preparing download...');
      // This would typically trigger a file download
      // The API endpoint is: design/design-items/:designItemId/download
      // But this requires user to be verified JWT
      await Axios.get(`design/design-items/${designItemId}/download`, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `design-order-${orderId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      });
      Notify.success('Download started!');
    } catch (error) {
      Notify.failure('Failed to download files');
    } finally {
      Loading.remove();
    }
  };

  const ordersColumns = [
    { Header: 'ID', accessor: 'sr', width: '5%' },
    { Header: 'User', accessor: 'user', width: '15%' },
    { Header: 'Design', accessor: 'design', width: '20%' },
    { Header: 'Pricing Type', accessor: 'pricingType', width: '12%' },
    { Header: 'Amount', accessor: 'amount', width: '10%' },
    { Header: 'Status', accessor: 'status', width: '12%' },
    { Header: 'Ordered', accessor: 'orderedOn', width: '12%' },
    { Header: 'Actions', accessor: 'action', width: '14%' },
  ];

  const getOrderRow = (order, idx) => ({
    sr: idx + 1,
    user: (
      <MDTypography variant="caption">
        {order.user?.firstName || 'Unknown'} {order.user?.lastName || ''}
      </MDTypography>
    ),
    design: (
      <MDTypography variant="caption">
        {order.designItem?.title || 'N/A'}
      </MDTypography>
    ),
    pricingType: (
      <MDTypography variant="caption">
        {order.designPricing?.licensingType || 'N/A'}
      </MDTypography>
    ),
    amount: (
      <MDTypography variant="caption" color="success">
        ${parseFloat(order.amount || 0).toFixed(2)}
      </MDTypography>
    ),
    status: (
      <MDBadge
        badgeContent={order.status || 'pending'}
        color={
          order.status === 'completed' ? 'success' :
          order.status === 'processing' ? 'info' :
          order.status === 'failed' ? 'error' :
          'warning'
        }
        size="sm"
      />
    ),
    orderedOn: (
      <MDTypography variant="caption">
        {moment(order.createdAt).format('DD/MM/YYYY')}
      </MDTypography>
    ),
    action: (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => handleDownload(order.designItemId, order.id)}
          color="info"
          title="Download Files"
        >
          <Download />
        </IconButton>
        {canUpdate && (
          <IconButton
            size="small"
            onClick={() => handleEditStatus(order)}
            color="info"
            title="Update Status"
          >
            <Edit />
          </IconButton>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const closeStatusDialog = () => {
    setOpenStatusDialog(false);
    resetStatus();
    setSelectedOrder(null);
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Design Orders. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <MDTypography variant="h6" color="white">
                  Design Orders
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: ordersColumns,
                    rows: orders.map((order, idx) => getOrderRow(order, idx)),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  hoverEffect
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Update Status Dialog */}
      <Dialog open={openStatusDialog} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitStatus(handleStatusUpdate)}>
            <TextField
              select
              label="Status *"
              fullWidth
              margin="normal"
              {...registerStatus('status', { required: true })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </TextField>

            <DialogActions sx={{ mt: 3 }}>
              <MDButton onClick={closeStatusDialog}>Cancel</MDButton>
              <MDButton type="submit" color="info" variant="contained">
                Update Status
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default DesignOrders;
